import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import "@/App.css";
import SEO from "../components/shared/SEO";

// Navigation Component
const Navigation = () => (
  <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-white/5">
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4">
      <div className="flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <div className="flex gap-1">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="w-1.5 rounded-full bg-[#D4AF37]"
                style={{ height: `${12 + i * 6}px` }}
              />
            ))}
          </div>
          <div>
            <span className="text-white font-bold tracking-tight">LRS</span>
            <span className="hidden sm:block text-[10px] text-zinc-500 uppercase tracking-widest">Leitner Revenue Systems</span>
          </div>
        </Link>
        
        <Link
          to="/"
          className="flex items-center gap-2 text-zinc-400 hover:text-[#D4AF37] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>
      </div>
    </div>
  </nav>
);

// Footer Component
const Footer = () => (
  <footer className="bg-[#0A0A0A] border-t border-white/5 py-12">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="flex flex-col md:flex-row justify-between items-center gap-6">
        <div className="flex items-center gap-3">
          <div className="flex gap-1">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="w-1.5 rounded-full bg-[#D4AF37]"
                style={{ height: `${8 + i * 4}px` }}
              />
            ))}
          </div>
          <span className="text-white font-bold">LRS</span>
        </div>
        <p className="text-sm text-zinc-500">
          © {new Date().getFullYear()} Leitner Revenue Systems. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

// Approach Page
function ApproachPage() {
  const principles = [
    {
      title: "Systems over tactics",
      desc: "Tactics don't scale. Systems do. I build infrastructure that compounds."
    },
    {
      title: "Execution over tools",
      desc: "Tools amplify what you already do. If execution is broken, better tools make it worse faster."
    },
    {
      title: "Economics over activity",
      desc: "Pipeline is meaningless without conversion. I measure success by revenue impact, not meetings booked."
    },
    {
      title: "Talent over hiring",
      desc: "The best AEs were built, not bought. I design systems that develop closers internally."
    }
  ];

  const beliefs = [
    "AI doesn't fix broken systems—it scales them.",
    "Most teams scale inefficiency, not effectiveness.",
    "More activity without better execution is just more noise.",
    "The SDR function is a revenue system, not a cost center.",
    "Conversion rate is the most underrated metric in GTM."
  ];

  return (
    <div className="bg-[#0A0A0A] min-h-screen">
      <SEO 
        title="Approach | How I Think About Revenue"
        description="A point of view on what actually works in building pipeline and converting it. Core principles for GTM execution."
        path="/approach"
        image="home"
      />
      <Navigation />
      
      {/* Hero */}
      <section className="pt-32 pb-16 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-white tracking-tight mb-6">
            How I Think About Revenue
          </h1>
          <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
            A point of view on what actually works in building pipeline and converting it.
          </p>
        </div>
      </section>
      
      {/* Core Principles */}
      <section className="py-16 bg-[#1C1C1C]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <h2 className="text-2xl font-bold text-white mb-8">Core Principles</h2>
          
          <div className="space-y-6">
            {principles.map((item) => (
              <div key={item.title} className="p-6 bg-[#0A0A0A] rounded-xl border border-white/5">
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-zinc-400">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Beliefs */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <h2 className="text-2xl font-bold text-white mb-8">What I Believe</h2>
          
          <div className="space-y-4">
            {beliefs.map((belief) => (
              <p key={belief} className="text-xl text-white/80 leading-relaxed">
                {belief}
              </p>
            ))}
          </div>
        </div>
      </section>
      
      {/* On AI */}
      <section className="py-16 bg-[#1C1C1C]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <h2 className="text-2xl font-bold text-white mb-6">On AI in GTM</h2>
          
          <div className="space-y-4 text-zinc-400">
            <p>
              AI is powerful when applied to a working system. It's dangerous when used to paper over a broken one.
            </p>
            <p>
              I use AI where it creates leverage: research, personalization at scale, pattern recognition. 
              I don't use it to replace the human judgment that closes deals.
            </p>
            <p>
              The teams that win with AI are the ones who already know how to execute. 
              AI just makes them faster.
            </p>
          </div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
            Let's Talk
          </h2>
          <p className="text-zinc-400 mb-8">
            If this resonates, let's discuss what you're building.
          </p>
          <Link
            to="/#contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] text-[#0A0A0A] rounded-lg font-semibold hover:bg-[#E8C547] transition-all"
          >
            Get in Touch
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}

export default ApproachPage;
